import { useMemo, useRef, type TouchEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { JournalDayView } from '../components/journal/JournalDayView';
import { DatePickerButton } from '../components/ui/DatePickerButton';

function parseDateParam(raw?: string): Date {
  if (!raw) return startOfDay(new Date());
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return startOfDay(new Date());
  const [_, y, m, d] = match;
  const parsed = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(parsed.getTime()) ? startOfDay(new Date()) : startOfDay(parsed);
}

function toDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export default function JournalDayPage() {
  const { user } = useAuth();
  const { date } = useParams<{ date?: string }>();
  const navigate = useNavigate();
  const selectedDate = useMemo(() => parseDateParam(date), [date]);
  const today = useMemo(() => startOfDay(new Date()), []);
  const isAtToday = selectedDate.getTime() >= today.getTime();
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  function goByDays(offset: number) {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + offset);
    // Never navigate past today.
    if (next.getTime() > today.getTime()) return;
    navigate(`/journal/${toDateParam(next)}`);
  }

  function handleTouchStart(event: TouchEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement | null;
    if (target?.closest('input,textarea,button,a,[data-no-swipe="true"]')) {
      touchStartRef.current = null;
      return;
    }
    const touch = event.changedTouches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }

  function handleTouchEnd(event: TouchEvent<HTMLDivElement>) {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absX < 72 || absX < absY * 1.25) return;
    if (deltaX < 0) {
      goByDays(1);
    } else {
      goByDays(-1);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <header className="sticky top-0 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)] px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/')}
            className="p-2 -ml-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="font-semibold text-[var(--color-text-primary)]">Journal</h1>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => goByDays(-1)}
            className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)]"
            aria-label="Previous day"
          >
            <ChevronLeft size={16} />
          </button>
          <DatePickerButton
            date={selectedDate}
            onChange={(picked) => navigate(`/journal/${toDateParam(picked)}`)}
            max={new Date()}
          />
          <button
            onClick={() => goByDays(1)}
            disabled={isAtToday}
            className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent"
            aria-label="Next day"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </header>

      <div
        className="max-w-2xl mx-auto px-4 py-6"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <p className="text-[11px] text-[var(--color-text-muted)] mb-3 text-center">
          Swipe left or right to change day
        </p>
        <JournalDayView userId={user?.uid ?? null} date={selectedDate} />
      </div>
    </div>
  );
}
